import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';

const Login = () => {
  const navigate = useNavigate();
  const { signIn, isAuthenticated, loading } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [setupMessage, setSetupMessage] = useState('');
  const [isSettingUp, setIsSettingUp] = useState(false);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/compliance-overview');
    }
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
    if (setupMessage) setSetupMessage('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!formData?.email?.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData?.password?.trim()) {
      setError('Password is required');
      return;
    }

    setIsLoading(true);
    setError('');

    const { error: signInError } = await signIn(formData?.email, formData?.password);

    if (signInError) {
      setError(signInError?.message || 'Invalid email or password. Please try again.');
      setIsLoading(false);
    } else {
      navigate('/compliance-overview');
    }
  };

  const handleSetupAdmin = async () => {
    setIsSettingUp(true);
    setError('');
    setSetupMessage('');

    try {
      // Try to sign up the admin user
      const { data, error: signUpError } = await supabase?.auth?.signUp({
        email: 'admin@compliancetracker.com',
        password: 'Admin1234!',
        options: {
          data: { full_name: 'System Admin', role: 'Admin' }
        }
      });

      if (signUpError) {
        if (signUpError?.message?.toLowerCase()?.includes('already registered') ||
            signUpError?.message?.toLowerCase()?.includes('already exists')) {
          setSetupMessage('Admin account already exists. Try signing in with the demo credentials below.');
        } else {
          setError('Setup failed: ' + signUpError?.message);
        }
      } else if (data?.user) {
        // Also upsert the user_profiles record
        await supabase?.from('user_profiles')?.upsert({
          id: data?.user?.id,
          email: 'admin@compliancetracker.com',
          full_name: 'System Admin',
          role: 'Admin'
        }, { onConflict: 'id' });

        if (data?.session) {
          // User was auto-confirmed, navigate directly
          navigate('/compliance-overview');
        } else {
          setSetupMessage('✅ Admin account created! You can now sign in with the demo credentials below. (Check email if confirmation is required.)');
        }
      }
    } catch (err) {
      setError('Setup failed. Please try again.');
    } finally {
      setIsSettingUp(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 via-background to-secondary/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-primary/10 elevation-sm">
              <img
                src="/assets/images/Real_logo-1771587686730.png"
                alt="ComplianceTracker Logo"
                className="w-10 h-10 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
            ComplianceTracker
          </h1>
          <p className="text-sm text-muted-foreground">
            Employee Compliance Management System
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-card rounded-xl elevation-lg p-8">
          <div className="mb-6">
            <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
              Sign In
            </h2>
            <p className="text-sm text-muted-foreground">
              Enter your credentials to access the system
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <p className="text-sm text-destructive flex items-center gap-2">
                  <span>⚠</span>
                  {error}
                </p>
              </div>
            )}

            {setupMessage && (
              <div className="p-3 rounded-lg bg-green-50 border border-green-200">
                <p className="text-sm text-green-700">
                  {setupMessage}
                </p>
              </div>
            )}

            <Input
              type="email"
              name="email"
              label="Email Address"
              placeholder="Enter your email"
              value={formData?.email}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="email"
            />

            <Input
              type="password"
              name="password"
              label="Password"
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleChange}
              required
              disabled={isLoading}
              autoComplete="current-password"
            />

            <Button
              type="submit"
              fullWidth
              loading={isLoading}
              iconName="LogIn"
              size="lg"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 p-3 rounded-lg bg-muted/50 border border-border">
            <p className="text-xs font-medium text-muted-foreground mb-1">Demo Credentials:</p>
            <p className="text-xs text-foreground">Email: <span className="font-mono">admin@compliancetracker.com</span></p>
            <p className="text-xs text-foreground">Password: <span className="font-mono">Admin1234!</span></p>
          </div>

          {/* First-time setup */}
          <div className="mt-4 p-3 rounded-lg bg-blue-50 border border-blue-200">
            <p className="text-xs font-medium text-blue-700 mb-2">First time setup?</p>
            <p className="text-xs text-blue-600 mb-3">
              If this is a fresh deployment, click below to create the admin account in Supabase Auth.
            </p>
            <button
              type="button"
              onClick={handleSetupAdmin}
              disabled={isSettingUp}
              className="w-full text-xs font-medium text-blue-700 border border-blue-300 rounded-lg py-2 px-3 hover:bg-blue-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSettingUp ? (
                <>
                  <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin" />
                  Setting up...
                </>
              ) : (
                '⚙ Create Admin Account'
              )}
            </button>
          </div>
        </div>

        <div className="text-center mt-6">
          <p className="text-xs text-muted-foreground">
            ComplianceTracker v2.0.0 | Secure Access Portal
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;